import { Component, computed, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';

import { ButtonModule } from 'primeng/button';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { Router } from '@angular/router';
import { MessageModule } from 'primeng/message';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ODataServiceFactory } from 'angular-odata';
import { CustomerOrganisation, ProductionSite } from '@workspace-app/odatabackend/QualicoatBackendModel';
import {
  AutocompleteComponent,
  CalendarComponent,
  PageComponent,
  SelectComponent,
  SelectOption,
  ToastService,
  toUtcDateString,
} from '../../../../shared';
import { ApprovedSystemsService, CreateSystemRequest } from '../../services/approved-systems.service';
import { PowderLabelService } from '../../services/powder-label.service';

// System type constants matching backend
const SYSTEM_TYPES = {
  COATING_SYSTEM: 'Coating System',
  CHEMICAL_PRETREATMENT: 'Chemical Pretreatment System',
  CHEMICAL_PRETREATMENT_NEW_TECH: 'Chemical Pretreatment System (for new technology)',
  CHEMICAL_PRETREATMENT_PRE_OX: 'Chemical Pretreatment System (for pre anodised material)',
  POSTFORMING_COATING: 'Postforming Coating System',
  ORGANIC_COATING_SUBLIMATION: 'Organic Coating System for Sublimation',
};

// Organisation types matching backend
const ORG_TYPES = {
  POWDER_MANUFACTURER: 'PowderManufacturer',
  CHEMICAL_MANUFACTURER: 'ChemicalManufacturer',
};

// Mapping of system types to required organisation types
const SYSTEM_TYPE_TO_ORG_TYPE: Record<string, string> = {
  [SYSTEM_TYPES.COATING_SYSTEM]: ORG_TYPES.POWDER_MANUFACTURER,
  [SYSTEM_TYPES.POSTFORMING_COATING]: ORG_TYPES.POWDER_MANUFACTURER,
  [SYSTEM_TYPES.ORGANIC_COATING_SUBLIMATION]: ORG_TYPES.POWDER_MANUFACTURER,
  [SYSTEM_TYPES.CHEMICAL_PRETREATMENT]: ORG_TYPES.CHEMICAL_MANUFACTURER,
  [SYSTEM_TYPES.CHEMICAL_PRETREATMENT_NEW_TECH]: ORG_TYPES.CHEMICAL_MANUFACTURER,
  [SYSTEM_TYPES.CHEMICAL_PRETREATMENT_PRE_OX]: ORG_TYPES.CHEMICAL_MANUFACTURER,
};

// Check if system type is a pretreatment type
const isPretreatmentType = (systemType: string): boolean => {
  return (
    systemType === SYSTEM_TYPES.CHEMICAL_PRETREATMENT ||
    systemType === SYSTEM_TYPES.CHEMICAL_PRETREATMENT_NEW_TECH ||
    systemType === SYSTEM_TYPES.CHEMICAL_PRETREATMENT_PRE_OX
  );
};

// Check if system type is a coating type (powder-based)
const isCoatingType = (systemType: string): boolean => {
  return (
    systemType === SYSTEM_TYPES.COATING_SYSTEM ||
    systemType === SYSTEM_TYPES.POSTFORMING_COATING ||
    systemType === SYSTEM_TYPES.ORGANIC_COATING_SUBLIMATION
  );
};

@Component({
  selector: 'app-create-approved-system',
  standalone: true,
  imports: [
    ButtonModule,
    PageComponent,
    ReactiveFormsModule,
    TooltipModule,
    SelectComponent,
    AutocompleteComponent,
    CalendarComponent,
    MessageModule,
    InputTextModule,
    CheckboxModule,
    InputGroup,
    InputGroupAddonModule,
  ],
  templateUrl: './create-approved-system.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./create-approved-system.component.scss'],
})
export class CreateApprovedSystemComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private approvedSystemsService = inject(ApprovedSystemsService);
  private toastService = inject(ToastService);
  private odataFactory = inject(ODataServiceFactory);
  private labelService = inject(PowderLabelService);

  // Form state using signals
  organisations = signal<CustomerOrganisation[]>([]);
  productionSites = signal<ProductionSite[]>([]);
  productionSiteOptions = signal<SelectOption[]>([]);
  selectedSystemType = signal<string>('');

  // System number validation state
  systemNumberValid = signal<boolean | null>(null);
  systemNumberError = signal<string | null>(null);
  formattedSystemNumber = signal<string>('');
  checkingNumber = signal<boolean>(false);

  // System type options
  systemTypeOptions: SelectOption[] = [
    { label: 'P-No (Coating System)', value: SYSTEM_TYPES.COATING_SYSTEM },
    { label: 'A-No (Chemical Pretreatment)', value: SYSTEM_TYPES.CHEMICAL_PRETREATMENT },
    { label: 'AN-No (Chemical Pretreatment - New Tech)', value: SYSTEM_TYPES.CHEMICAL_PRETREATMENT_NEW_TECH },
    { label: 'AP-No (Chemical Pretreatment - Pre-Ox)', value: SYSTEM_TYPES.CHEMICAL_PRETREATMENT_PRE_OX },
    { label: 'PF-No (Postforming Coating)', value: SYSTEM_TYPES.POSTFORMING_COATING },
    { label: 'DP-No (Organic Coating for Sublimation)', value: SYSTEM_TYPES.ORGANIC_COATING_SUBLIMATION },
  ];

  // Coating system dropdown options
  pClassOptions: SelectOption[] = [
    { label: this.labelService.classLabels[1], value: 1 },
    { label: this.labelService.classLabels[1.5], value: 1.5 },
    { label: this.labelService.classLabels[2], value: 2 },
    { label: this.labelService.classLabels[3], value: 3 },
  ];

  glossCategoryOptions: SelectOption[] = [
    { label: this.labelService.glossCategoryLabels[1], value: 1 },
    { label: this.labelService.glossCategoryLabels[2], value: 2 },
    { label: this.labelService.glossCategoryLabels[3], value: 3 },
  ];

  structureFinishOptions: SelectOption[] = [
    { label: this.labelService.structureFinishLabels['L'], value: 'L' },
    { label: this.labelService.structureFinishLabels['T'], value: 'T' },
    { label: this.labelService.structureFinishLabels['W'], value: 'W' },
    { label: this.labelService.structureFinishLabels['N'], value: 'N' },
  ];

  approvalTypeOptions: SelectOption[] = [
    { label: this.labelService.approvalTypeLabels[1], value: 1 },
    { label: this.labelService.approvalTypeLabels[2], value: 2 },
    { label: this.labelService.approvalTypeLabels[3], value: 3 },
  ];

  coatingMaterialOptions: SelectOption[] = [
    { label: this.labelService.coatingMaterialLabels[1], value: 1 },
    { label: this.labelService.coatingMaterialLabels[2], value: 2 },
    { label: this.labelService.coatingMaterialLabels[3], value: 3 },
  ];

  // Computed properties
  isPretreatmentSystem = computed(() => {
    const systemType = this.selectedSystemType();
    return systemType ? isPretreatmentType(systemType) : false;
  });

  isCoatingSystem = computed(() => {
    const systemType = this.selectedSystemType();
    return systemType ? isCoatingType(systemType) : false;
  });

  // Class-3 coating systems are created in the Granting state with no Number and no approval date;
  // those fields are assigned when the system transitions to Provisional via the GrantProvisional trigger.
  selectedPClass = signal<number | null>(null);
  isClass3Coating = computed(() => this.isCoatingSystem() && this.selectedPClass() === 3);

  showProductionSiteSelector = computed(() => {
    return this.productionSiteOptions().length > 1;
  });

  hasSelectedSupplier = computed(() => {
    return !!this.systemForm.get('supplierId')?.value;
  });

  systemForm = this.fb.group({
    systemType: ['', Validators.required],
    systemNumber: [null as number | null, [Validators.required, Validators.min(1)]],
    supplier: ['', Validators.required], // For autocomplete display
    supplierId: ['', Validators.required],
    productionSiteId: ['', Validators.required],
    systemName: ['', [Validators.required, Validators.maxLength(100)]],
    approvalDate: [new Date(), Validators.required],
    // Pretreatment-specific fields (optional)
    outdoorExposureTestStartDate: [null as Date | null],
    rinseProcess: [''],
    // Coating system-specific fields (optional)
    pCode: [''],
    pClass: [null as number | null],
    glossCategory: [null as number | null],
    structureFinish: [null as string | null],
    coatingMaterial: [null as number | null],
    approvalType: [null as number | null],
    metallicColoursApproved: [false],
  });

  ngOnInit(): void {
    // Watch for system type changes
    this.systemForm.get('systemType')?.valueChanges.subscribe((systemType) => {
      this.selectedSystemType.set(systemType || '');
      if (systemType) {
        this.applyClass3Adjustments();
        if (!this.isClass3Coating()) {
          this.fetchNextSystemNumber(systemType);
        }
        this.clearSupplierAndSite();
      }
    });

    // Watch for pClass changes so the form can switch to the class-3 Granting flow.
    this.systemForm.get('pClass')?.valueChanges.subscribe((value) => {
      const numeric = value == null ? null : Number(value);
      this.selectedPClass.set(numeric);
      this.applyClass3Adjustments();
      const systemType = this.systemForm.get('systemType')?.value;
      if (systemType && !this.isClass3Coating() && !this.systemForm.get('systemNumber')?.value) {
        this.fetchNextSystemNumber(systemType);
      }
    });

    // Watch for system number changes to validate on blur
    this.systemForm.get('systemNumber')?.valueChanges.subscribe(() => {
      // Reset validation state when number changes
      this.systemNumberValid.set(null);
      this.systemNumberError.set(null);
    });
  }

  private applyClass3Adjustments(): void {
    const numberControl = this.systemForm.get('systemNumber');
    const approvalDateControl = this.systemForm.get('approvalDate');
    if (!numberControl || !approvalDateControl) return;

    if (this.isClass3Coating()) {
      numberControl.clearValidators();
      numberControl.setValue(null, { emitEvent: false });
      approvalDateControl.clearValidators();
      approvalDateControl.setValue(null, { emitEvent: false });
      this.systemNumberValid.set(null);
      this.systemNumberError.set(null);
      this.formattedSystemNumber.set('');
    } else {
      numberControl.setValidators([Validators.required, Validators.min(1)]);
      approvalDateControl.setValidators([Validators.required]);
      if (approvalDateControl.value == null) {
        approvalDateControl.setValue(new Date(), { emitEvent: false });
      }
    }
    numberControl.updateValueAndValidity({ emitEvent: false });
    approvalDateControl.updateValueAndValidity({ emitEvent: false });
  }

  searchSupplier(query: string): void {
    const systemType = this.systemForm.get('systemType')?.value;
    if (!systemType) {
      return;
    }

    const requiredOrgType = SYSTEM_TYPE_TO_ORG_TYPE[systemType];
    const organisationService = this.odataFactory.entitySet<CustomerOrganisation>(
      'CustomerOrganisations',
      'QualicoatBackend.Domain.Crm.OrganisationTypes.CustomerOrganisation'
    );

    organisationService
      .entities()
      ?.query((q) => {
        q.filter(`contains(tolower(name),tolower('${query}')) and organisationType eq '${requiredOrgType}'`);
        q.top(20);
      })
      .fetch()
      .subscribe((res) => {
        this.organisations.set(res.entities || []);
      });
  }

  supplierSelected(organisation: CustomerOrganisation): void {
    this.systemForm.patchValue({
      supplier: organisation.name,
      supplierId: organisation.id,
      productionSiteId: '',
    });

    // Load production sites for this organisation
    this.loadProductionSites(organisation.id!);
  }

  onSystemNumberBlur(): void {
    const systemType = this.systemForm.get('systemType')?.value;
    const number = this.systemForm.get('systemNumber')?.value;

    if (!systemType || !number || number <= 0) {
      return;
    }

    this.checkingNumber.set(true);
    this.approvedSystemsService.checkNumberExists(systemType, number).subscribe({
      next: (response) => {
        this.checkingNumber.set(false);
        this.formattedSystemNumber.set(response.formattedNumber);
        if (response.exists) {
          this.systemNumberValid.set(false);
          this.systemNumberError.set(`System number ${response.formattedNumber} already exists`);
        } else {
          this.systemNumberValid.set(true);
          this.systemNumberError.set(null);
        }
      },
      error: () => {
        this.checkingNumber.set(false);
        this.systemNumberValid.set(null);
        this.systemNumberError.set(null);
      },
    });
  }

  createSystem(): void {
    if (this.systemForm.invalid || this.systemNumberValid() === false) {
      return;
    }

    const formValue = this.systemForm.value;
    const approvalDate = formValue.approvalDate as Date | null;
    const class3 = this.isClass3Coating();

    const request: CreateSystemRequest = {
      systemType: formValue.systemType!,
      productionSiteId: formValue.productionSiteId!,
      system: formValue.systemName!,
    };

    if (!class3) {
      request.number = formValue.systemNumber!;
      if (approvalDate) {
        request.approvalDate = toUtcDateString(approvalDate) ?? undefined;
      }
    }

    // Add pretreatment-specific fields if applicable
    if (this.isPretreatmentSystem()) {
      if (formValue.outdoorExposureTestStartDate) {
        request.outdoorExposureTestStartDate = toUtcDateString(formValue.outdoorExposureTestStartDate) ?? undefined;
      }
      if (formValue.rinseProcess) {
        request.rinseProcess = formValue.rinseProcess;
      }
    }

    // Add coating system-specific fields if applicable
    if (this.isCoatingSystem()) {
      if (formValue.pCode) {
        request.pCode = formValue.pCode;
      }
      if (formValue.pClass !== null && formValue.pClass !== undefined) {
        request.pClass = formValue.pClass;
      }
      if (formValue.glossCategory !== null && formValue.glossCategory !== undefined) {
        request.glossCategory = formValue.glossCategory;
      }
      if (formValue.structureFinish) {
        request.structureFinish = formValue.structureFinish;
      }
      if (formValue.coatingMaterial !== null && formValue.coatingMaterial !== undefined) {
        request.coatingMaterial = formValue.coatingMaterial;
      }
      if (formValue.approvalType !== null && formValue.approvalType !== undefined) {
        request.approvalType = formValue.approvalType;
      }
      if (formValue.metallicColoursApproved) {
        request.metallicColoursApproved = formValue.metallicColoursApproved;
      }
    }

    this.approvedSystemsService.createSystem(request).subscribe({
      next: (result) => {
        this.toastService.displaySuccessMessage(`System ${result.completeApprovalNumber} created successfully`);
        this.router.navigate(['/certification/approved-systems']);
      },
      error: (err) => {
        const message = err.error?.message || 'Failed to create system';
        this.toastService.displayErrorMessage(message);
      },
    });
  }

  // Private methods

  private fetchNextSystemNumber(systemType: string): void {
    this.approvedSystemsService.getNextSystemNumber(systemType).subscribe({
      next: (response) => {
        this.systemForm.patchValue({ systemNumber: response.nextNumber });
        this.formattedSystemNumber.set(response.formattedNumber);
        this.systemNumberValid.set(true);
        this.systemNumberError.set(null);
      },
      error: () => {
        this.formattedSystemNumber.set('');
        this.systemNumberValid.set(null);
      },
    });
  }

  private loadProductionSites(organisationId: string): void {
    const service = this.odataFactory.entitySet<ProductionSite>('ProductionSites');
    service
      .entities()
      .query((q) => {
        q.filter(`customerId eq ${organisationId}`);
        q.orderBy('name');
      })
      .fetch()
      .subscribe((res) => {
        const sites = res.entities || [];
        this.productionSites.set(sites);
        this.productionSiteOptions.set(
          sites.map((ps) => ({
            label: ps.name || 'Unnamed Site',
            value: ps.id,
          }))
        );

        // Auto-select if only one production site
        if (sites.length === 1) {
          this.systemForm.patchValue({ productionSiteId: sites[0].id });
        } else if (sites.length === 0) {
          this.toastService.displayErrorMessage(
            'This organisation has no production sites. Please add a production site first.'
          );
        }
      });
  }

  private clearSupplierAndSite(): void {
    this.systemForm.patchValue({
      supplier: '',
      supplierId: '',
      productionSiteId: '',
    });
    this.organisations.set([]);
    this.productionSites.set([]);
    this.productionSiteOptions.set([]);
  }
}
